/**
 * BoardGruppi.tsx — l'elenco del Board Giornaliero, raggruppato per urgenza.
 *
 * Il board e' la riunione di avvio giornata: si decide cosa fare oggi sui
 * veicoli in garage, e la nota su ogni scheda e' il risultato di quella
 * decisione. Una lista piatta di 72 righe dice cosa c'e', non di cosa parlare:
 * i gruppi mettono in cima le cose che aprono la riunione.
 *
 * Struttura e linguaggio visivo vengono dalla pagina "Lavori in officina"
 * (pages/schede-lavoro/LavoriInOfficina.tsx): stessi gruppi collassabili con
 * pallino e conteggio, stesse righe a scheda con i tag dei meta.
 */
import React, { useMemo } from 'react'
import { Button, Collapse, Input, Tag, Typography, theme } from 'antd'
import dayjs from 'dayjs'
import type { WorkOrder } from '@/types'

const { Text } = Typography

// L'intervento come arriva da /work-orders/interventi-attivi
export interface InterventoBoard {
  id: number
  work_order_id: number
  progressivo: number | null
  descrizione_intervento: string
  stato_codice: string | null
  stato_nome: string | null
}

export type GruppoBoard = 'scadenza' | 'valutare' | 'ferme' | 'lavorazione' | 'altre'

// Un intervento "aperto" e' uno su cui c'e' ancora da lavorare. I completati
// non contano: una scheda con soli interventi conclusi non ha lavoro in corso.
const STATI_APERTI = new Set(['non_iniziato', 'iniziato', 'sospeso'])

/**
 * In quale gruppo va una scheda. La PRIMA regola che combacia vince: una scheda
 * in scadenza con interventi fermi sta in "In scadenza", perche' quella e'
 * l'urgenza di cui parlare.
 */
export function gruppoDiScheda(wo: WorkOrder, interventi: InterventoBoard[]): GruppoBoard {
  // 1. Consegna prevista oggi o gia' passata. Senza data non si finisce mai qui:
  //    e' accettato, e l'assenza del dato si vede sulla riga.
  if (wo.data_fine_prevista && !dayjs(wo.data_fine_prevista).isAfter(dayjs(), 'day')) {
    return 'scadenza'
  }

  const aperti = interventi.filter(i => STATI_APERTI.has(i.stato_codice ?? ''))

  // 2. Nessun lavoro aperto: il veicolo e' arrivato e non si e' deciso niente.
  //    E' il primo argomento vero della riunione, per questo sta cosi' in alto.
  if (aperti.length === 0) return 'valutare'

  const qualcunoIniziato = aperti.some(i => i.stato_codice === 'iniziato')
  const qualcunoSospeso = aperti.some(i => i.stato_codice === 'sospeso')

  // 3. Ferme: qualcosa e' bloccato e nessuno ci sta lavorando.
  if (qualcunoSospeso && !qualcunoIniziato) return 'ferme'
  // 4. In lavorazione: c'e' almeno una mano sopra.
  if (qualcunoIniziato) return 'lavorazione'
  // 5. Le altre: interventi aperti ma nessuno ancora cominciato.
  return 'altre'
}

const GRUPPI: { chiave: GruppoBoard; titolo: string; tono: 'error' | 'warning' | 'primary' | 'neutro' }[] = [
  { chiave: 'scadenza',    titolo: 'In scadenza',    tono: 'error' },
  { chiave: 'valutare',    titolo: 'Da valutare',    tono: 'warning' },
  { chiave: 'ferme',       titolo: 'Ferme',          tono: 'warning' },
  { chiave: 'lavorazione', titolo: 'In lavorazione', tono: 'primary' },
  { chiave: 'altre',       titolo: 'Le altre',       tono: 'neutro' },
]

const dataBreve = (d?: string | null) => (d ? dayjs(d).format('DD/MM') : null)

const TAG_STATO_INTERVENTO: Record<string, string> = {
  iniziato: 'processing',
  sospeso: 'warning',
  completato: 'success',
  non_iniziato: 'default',
}

interface RigaProps {
  wo: WorkOrder
  interventi: InterventoBoard[]
  gruppo: GruppoBoard
  officine: string[]
  autoCortesia?: { targa: string; marca?: string; modello?: string }
  notaTesto: string
  salvandoNota: boolean
  onNotaChange: (testo: string) => void
  onNotaSave: () => void
  onApri: () => void
}

const RigaScheda: React.FC<RigaProps> = ({
  wo, interventi, gruppo, officine, autoCortesia, notaTesto, salvandoNota,
  onNotaChange, onNotaSave, onApri,
}) => {
  const { token } = theme.useToken()

  const coloreBordo =
    gruppo === 'scadenza' ? token.colorError
    : gruppo === 'ferme' || gruppo === 'valutare' ? token.colorWarning
    : gruppo === 'lavorazione' ? token.colorPrimary
    : token.colorBorder

  const mancante = (testo: string) => (
    <Text style={{ fontSize: 12, fontStyle: 'italic', color: token.colorTextQuaternary }}>{testo}</Text>
  )

  return (
    <div
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderLeft: `3px solid ${coloreBordo}`,
        borderRadius: token.borderRadius,
        margin: 8,
        padding: '10px 14px',
      }}
    >
      {/* Chi e' */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <Text strong style={{ fontFamily: 'monospace', fontSize: 14 }}>{wo.vehicle_targa ?? '—'}</Text>
        <Text style={{ fontSize: 13, color: token.colorTextSecondary }}>
          {[wo.vehicle_marca, wo.vehicle_modello].filter(Boolean).join(' ')}
        </Text>
        <Text style={{ fontSize: 13, color: token.colorTextSecondary }}>{wo.customer_nome}</Text>
        <Tag style={{ margin: 0, fontSize: 11 }}>{wo.stato}</Tag>
        <Button size="small" type="link" style={{ marginLeft: 'auto', padding: 0 }} onClick={onApri}>
          scheda {wo.numero_scheda}
        </Button>
      </div>

      {/* Da quanto e' qui, per quando e' promessa. Dati compilati di rado: quando
          mancano si dice, invece di lasciare un trattino muto. */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 5 }}>
        <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>
          in officina dal{' '}
          {dataBreve(wo.data_entrata_veicolo) ?? mancante('non registrata')}
        </Text>
        {officine.map(nome => (
          <Tag key={nome} color="purple" style={{ margin: 0, fontSize: 11 }}>{nome}</Tag>
        ))}
        {autoCortesia && (
          <Tag color="cyan" style={{ margin: 0, fontSize: 11 }}>
            cortesia {autoCortesia.targa}
          </Tag>
        )}
        <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>
          consegna{' '}
          {wo.data_fine_prevista
            ? (
              <Text style={{
                fontSize: 12,
                fontWeight: 500,
                color: gruppo === 'scadenza' ? token.colorError : token.colorTextSecondary,
              }}>
                {dataBreve(wo.data_fine_prevista)}
              </Text>
            )
            : mancante('non indicata')}
        </Text>
      </div>

      {/* Di cosa si sta parlando mentre si scrive la nota */}
      <div style={{ marginTop: 8 }}>
        {interventi.length === 0
          ? <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>Nessun intervento sulla scheda</Text>
          : interventi.map(iv => (
            <div key={iv.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12, marginBottom: 2 }}>
              <Text style={{ color: token.colorTextTertiary, minWidth: 16 }}>{iv.progressivo ?? '·'}</Text>
              <Text style={{ flex: 1, color: token.colorText }}>{iv.descrizione_intervento}</Text>
              <Tag
                color={TAG_STATO_INTERVENTO[iv.stato_codice ?? ''] ?? 'default'}
                style={{ margin: 0, fontSize: 10 }}
              >
                {iv.stato_nome ?? 'senza stato'}
              </Tag>
            </div>
          ))}
      </div>

      {/* La nota del giorno: e' la funzione vera del board. Resta della SCHEDA
          (daily_work_order_notes, work_order_id + data), non dell'intervento. */}
      <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'flex-end' }}>
        <Input.TextArea
          value={notaTesto}
          onChange={e => onNotaChange(e.target.value)}
          placeholder="Nota di oggi per questo veicolo…"
          autoSize={{ minRows: 1, maxRows: 4 }}
          style={{ fontSize: 13 }}
        />
        <Button size="small" type="primary" loading={salvandoNota} onClick={onNotaSave}>
          Salva
        </Button>
      </div>
    </div>
  )
}

interface Props {
  workOrders: WorkOrder[]
  interventiPerScheda: Map<number, InterventoBoard[]>
  officinePerScheda: Map<number, string[]>
  autoCortesiaPerScheda: Map<number, { targa: string; marca?: string; modello?: string }>
  noteEdits: Map<number, string>
  savingNotes: Set<number>
  onNotaChange: (woId: number, testo: string) => void
  onNotaSave: (woId: number) => void
  onApriScheda: (woId: number) => void
}

const BoardGruppi: React.FC<Props> = ({
  workOrders, interventiPerScheda, officinePerScheda, autoCortesiaPerScheda,
  noteEdits, savingNotes, onNotaChange, onNotaSave, onApriScheda,
}) => {
  const { token } = theme.useToken()

  const perGruppo = useMemo(() => {
    const mappa: Record<GruppoBoard, WorkOrder[]> = {
      scadenza: [], valutare: [], ferme: [], lavorazione: [], altre: [],
    }
    for (const wo of workOrders) {
      mappa[gruppoDiScheda(wo, interventiPerScheda.get(wo.id) ?? [])].push(wo)
    }
    return mappa
  }, [workOrders, interventiPerScheda])

  const colorePallino = (tono: string) =>
    tono === 'error' ? token.colorError
    : tono === 'warning' ? token.colorWarning
    : tono === 'primary' ? token.colorPrimary
    : token.colorBorder

  return (
    <Collapse
      // I primi quattro aperti, "Le altre" chiuso: chi guarda vede subito cio'
      // di cui si deve parlare, e il resto resta a portata di clic.
      defaultActiveKey={['scadenza', 'valutare', 'ferme', 'lavorazione']}
      items={GRUPPI.map(g => ({
        key: g.chiave,
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
              background: colorePallino(g.tono),
            }} />
            <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>{g.titolo}</span>
            <span style={{ fontSize: 13, color: token.colorTextSecondary, fontVariantNumeric: 'tabular-nums' }}>
              {perGruppo[g.chiave].length}
            </span>
          </div>
        ),
        children: perGruppo[g.chiave].length === 0
          ? (
            <div style={{ padding: '14px 15px', textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 13 }}>Nessun veicolo in questo gruppo</Text>
            </div>
          )
          : (
            <div style={{ background: token.colorFillQuaternary, paddingTop: 1, paddingBottom: 1 }}>
              {perGruppo[g.chiave].map(wo => (
                <RigaScheda
                  key={wo.id}
                  wo={wo}
                  gruppo={g.chiave}
                  interventi={interventiPerScheda.get(wo.id) ?? []}
                  officine={officinePerScheda.get(wo.id) ?? []}
                  autoCortesia={autoCortesiaPerScheda.get(wo.id)}
                  notaTesto={noteEdits.get(wo.id) ?? ''}
                  salvandoNota={savingNotes.has(wo.id)}
                  onNotaChange={testo => onNotaChange(wo.id, testo)}
                  onNotaSave={() => onNotaSave(wo.id)}
                  onApri={() => onApriScheda(wo.id)}
                />
              ))}
            </div>
          ),
        styles: { body: { padding: 0 } },
      }))}
    />
  )
}

export default BoardGruppi
